/**
 * Mobile sticky ATC for XXL product templates.
 * Visible only when:
 *  - the customer has scrolled past the first rendered Product Card XXL buy button,
 *  - no Product Card XXL buy button is currently on screen,
 *  - the footer is not in view.
 */
class XXLStickyAtc extends HTMLElement {
  connectedCallback() {
    this.container = this.querySelector('.xxl-sticky-atc__container')
    this.footerVisible = false

    this.update = this.update.bind(this)
    this.observeBuyButtons = this.observeBuyButtons.bind(this)

    this.buyButtonsObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => this.entries.set(entry.target, entry))
      this.update()
    })
    this.observeBuyButtons()

    // Theme editor re-renders sections, replacing the observed buy buttons
    document.addEventListener('shopify:section:load', this.observeBuyButtons)
    document.addEventListener('shopify:section:unload', this.observeBuyButtons)

    const footer = document.querySelector('footer')
    if (footer) {
      this.footerObserver = new IntersectionObserver(
        ([entry]) => {
          this.footerVisible = entry.isIntersecting
          this.update()
        },
        { rootMargin: '100px 0px 0px 0px' }
      )
      this.footerObserver.observe(footer)
    }

    // Jumps (scroll-to-top, anchors, scroll restoration) can skip past a buy
    // button without it ever intersecting, so observers alone miss them
    this.onScroll = () => {
      if (this.scrollFrame) return
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null
        this.update()
      })
    }
    window.addEventListener('scroll', this.onScroll, { passive: true })

    // Buy buttons can be shown/hidden per breakpoint (visibility setting)
    this.mediaQuery = window.matchMedia('(min-width: 750px)')
    this.mediaQuery.addEventListener('change', this.update)
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.observeBuyButtons)
    document.removeEventListener('shopify:section:unload', this.observeBuyButtons)
    this.buyButtonsObserver?.disconnect()
    this.footerObserver?.disconnect()
    this.mediaQuery?.removeEventListener('change', this.update)
    window.removeEventListener('scroll', this.onScroll)
    cancelAnimationFrame(this.scrollFrame)
  }

  observeBuyButtons() {
    this.buyButtonsObserver.disconnect()
    this.entries = new Map()
    this.buyButtons = [...document.querySelectorAll('[data-xxl-atc]')]
    this.buyButtons.forEach((el) => this.buyButtonsObserver.observe(el))
  }

  isRendered(el) {
    return el.getClientRects().length > 0
  }

  update() {
    const rendered = this.buyButtons.filter((el) => this.isRendered(el))
    const first = rendered[0]

    let show = false

    if (first && !this.footerVisible) {
      const anyVisible = rendered.some((el) => this.entries.get(el)?.isIntersecting)
      const pastFirst = first.getBoundingClientRect().bottom < 0

      show = pastFirst && !anyVisible
    }

    this.classList.toggle('is-visible', show)
    this.container?.toggleAttribute('inert', !show)
  }
}

customElements.define('xxl-sticky-atc', XXLStickyAtc)
